import { ArrowRight, CalendarDays, MessageCircle, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import Seo from '../components/Seo';
import TextNowLink from '../components/TextNowLink';
import { EVENTS } from '../data/siteData';
import { ORGANIZATION_ID, SITE_URL, breadcrumbSchema } from '../lib/seoData';

const PAGE_PATH = '/halloween-holiday-trucks';
const PAGE_TITLE = 'Halloween & Holiday Tow Trucks Richmond BC';
const PAGE_DESCRIPTION = "Explore Jonny's Towing Halloween tow trucks, Christmas trucks, haunted community builds and memorable holiday experiences in Richmond, BC.";

export default function HolidayTrucks() {
  const structuredData = [
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Halloween & Holiday Trucks', path: PAGE_PATH },
    ]),
    {
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}${PAGE_PATH}#page`,
      url: `${SITE_URL}${PAGE_PATH}`,
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      about: { '@id': ORGANIZATION_ID },
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/images/halloween-truck.webp`,
      },
      inLanguage: 'en-CA',
    },
  ];

  return (
    <>
      <Seo
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        image="/images/halloween-truck.webp"
        canonicalPath={PAGE_PATH}
        structuredData={structuredData}
      />
      <PageHero
        eyebrow="A Jonny’s signature"
        title="Black trucks. Bright-pink accents. Holiday displays people remember."
        description="Towing is what we do. The Halloween and holiday builds are part of how Jonny’s Towing shows personality, creativity and community spirit across Richmond."
        image="/images/halloween-truck.webp"
      >
        <Link className="button button--primary" to="/in-the-community">See community projects <ArrowRight size={18} /></Link>
        <TextNowLink className="button button--ghost"><MessageCircle size={18} /> Need roadside help?</TextNowLink>
      </PageHero>

      <section className="section container feature-split">
        <div className="feature-image-wrap">
          <img src="/images/halloween-truck.webp" alt="Jonny's Towing Halloween decorated tow truck in Richmond BC" />
          <div className="feature-stat"><strong>Halloween at Jonny’s</strong><span>Recognizable trucks, original builds and community experiences</span></div>
        </div>
        <div className="feature-copy">
          <span className="eyebrow">Halloween tow trucks in Richmond</span>
          <h2>The spooky season is part of the Jonny’s identity.</h2>
          <p>Halloween is more than a colour change on the website. Jonny’s Towing has used decorated trucks, original haunted installations and community events to create experiences people recognize and remember.</p>
          <p>The same black-and-pink towing fleet customers know throughout the year becomes part of the seasonal experience while the roadside service itself stays professional, capable and focused on the customer.</p>
          <Link className="text-link" to="/services">See towing and roadside services <ArrowRight size={18} /></Link>
        </div>
      </section>

      <section className="section section--dark">
        <div className="container">
          <div className="section-heading centered-heading">
            <span className="eyebrow">Halloween history</span>
            <h2>Original Richmond experiences built year after year.</h2>
            <p>These projects are part of Jonny’s Towing’s community history and give the seasonal trucks a story beyond decoration alone.</p>
          </div>
          <div className="timeline">
            {EVENTS.map((event) => (
              <article className="timeline-card" key={event.title}>
                <div className="timeline-year"><CalendarDays /><span>{event.year}</span></div>
                <div>
                  <span className="eyebrow">{event.category}</span>
                  <h2>{event.title}</h2>
                  <p>{event.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section container feature-split feature-split--reverse">
        <div className="feature-copy">
          <Sparkles size={38} />
          <span className="eyebrow">Christmas and holiday trucks</span>
          <h2>The same unmistakable fleet gets a completely different holiday personality.</h2>
          <p>During the Christmas season, Jonny’s trucks trade the Halloween atmosphere for lights, festive details and neighbourhood appearances. The goal is the same: make a towing company feel human, local and memorable.</p>
          <p>That seasonal identity carries through the website and community projects without changing the core promise—careful vehicle handling, clear communication and dependable roadside assistance.</p>
          <Link className="button button--secondary" to="/jonnys-in-your-neighborhood">See holiday neighbourhood visits <ArrowRight size={18} /></Link>
        </div>
        <div className="feature-image-wrap feature-image-wrap--collage">
          <img src="/images/christmas-truck.webp" alt="Jonny's Towing Christmas decorated tow truck in Richmond BC" />
          <img src="/images/christmas-fleet.webp" alt="Jonny's Towing holiday decorated truck fleet" />
        </div>
      </section>

      <section className="cta-section">
        <div className="container cta-panel">
          <div>
            <span className="eyebrow">The memorable part is intentional</span>
            <h2>A tow truck can solve the problem and still make the day a little better.</h2>
            <p>That is the spirit behind the seasonal trucks—and the service behind them.</p>
          </div>
          <div className="hero-actions">
            <Link className="button button--primary" to="/experiences">The Jonny’s experience</Link>
            <TextNowLink className="button button--secondary"><MessageCircle size={18} /> Text Now</TextNowLink>
          </div>
        </div>
      </section>
    </>
  );
}
